import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DownloadIcon from "@material-ui/icons/CloudDownloadOutlined";
import clsx from "clsx";
import {
  CustomTable,
  CustomTableCell,
  CustomTableRow
} from "../../../components/common/CustomTable/CustomTable";
import { CollapseBlock } from "../../../components/common/CollapseBlock/collapseBlock";
import { CButton } from "../../../components/common/buttons";
import { Clickable } from "../../../components/common/Clickable";
import { Amount } from "../../../components/common/blocks/Amount";
import { useCustomSnackbar } from "../../../utils/hooks/useCustomSnackbar";
import { useDownloadFile } from "../../remonts/RemontInfo/Gpr/useDownloadFile";
import { PAGES_ROUTE_KEY } from "../../../constants";
import { AvrJournalFilters } from "./filters";
import {
  avrObjectLine,
  avrSignLine,
  canEditAvrNum,
  canSignAvr,
  cleanAvrParams,
  displayAvrNum,
  getAvrJournal,
  getAvrSignUrl,
  updateAvrIncomingDocNum
} from "../services";
import styles from "../styles.module.scss";

const SIGNED_QUERY_KEY = "avr_signed";

const headItems = [
  { label: "ID / объект", align: "left" },
  { label: "Акт", align: "left" },
  { label: "Контрагент", align: "left" },
  { label: "Договор", align: "left" },
  { label: "Сумма", align: "right" },
  { label: "Статус", align: "left" },
  { label: "", align: "center" }
];

const emptyFilters = remontId => ({
  status_code: remontId ? "ALL" : "",
  contractor_id: "",
  group_type_code: "",
  specification_num: "",
  flat_num: "",
  remont_id: remontId || "",
  contragent_avr_id: "",
  avr_date_from: "",
  avr_date_to: ""
});

export const AvrJournal = ({ remontId = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addDefaultSnack } = useCustomSnackbar();
  const { downloadFile } = useDownloadFile();

  const [filters, setFilters] = useState(() => emptyFilters(remontId));
  const [filtersKey, setFiltersKey] = useState(0);
  const [rows, setRows] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [signingId, setSigningId] = useState(null);
  const [downloadingIds, setDownloadingIds] = useState({});
  const [numDialog, setNumDialog] = useState(null);
  const [numSaving, setNumSaving] = useState(false);
  const signStartedRef = useRef(false);

  const getData = useCallback(async params => {
    setIsFetching(true);
    const res = await getAvrJournal(cleanAvrParams(params));
    setIsFetching(false);
    if (!res) return;
    setRows(res.rows);
    setContractors(res.contractors);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => getData(filters), 300);
    return () => clearTimeout(timer);
  }, [filters, getData]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (!query.has(SIGNED_QUERY_KEY)) return;
    addDefaultSnack("Подпись отправлена. Статус акта обновится через несколько секунд");
    query.delete(SIGNED_QUERY_KEY);
    const search = query.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate, addDefaultSnack]);

  useEffect(() => {
    // Подпись идёт в отдельной вкладке MyNCA: вернулись на эту — обновляем список.
    const onFocus = () => {
      if (!signStartedRef.current) return;
      signStartedRef.current = false;
      getData(filters);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [filters, getData]);

  const onFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value ?? "" }));
  };

  const onReset = () => {
    setFilters(emptyFilters(remontId));
    setFiltersKey(prev => prev + 1);
  };

  const openNumDialog = row => {
    setNumDialog({
      contragent_avr_id: row.contragent_avr_id,
      value: row.incoming_doc_num || ""
    });
  };

  const saveNum = async () => {
    if (!numDialog) return;
    setNumSaving(true);
    const res = await updateAvrIncomingDocNum(
      numDialog.contragent_avr_id,
      numDialog.value.trim()
    );
    setNumSaving(false);
    if (!res) return;
    setNumDialog(null);
    addDefaultSnack();
    getData(filters);
  };

  const handleSign = async row => {
    if (displayAvrNum(row) === "—") {
      addDefaultSnack("Подписать без номера нельзя. Укажите номер акта", "warning");
      openNumDialog(row);
      return;
    }
    setSigningId(row.contragent_avr_id);
    const returnQuery = new URLSearchParams(location.search);
    returnQuery.set(SIGNED_QUERY_KEY, row.contragent_avr_id);
    const returnUrl = `${window.location.origin}${location.pathname}?${returnQuery.toString()}`;
    const signUrl = await getAvrSignUrl(row.contragent_avr_id, returnUrl);
    setSigningId(null);
    if (!signUrl) return;
    signStartedRef.current = true;
    window.open(signUrl, "_blank");
  };

  const handleDownload = async row => {
    const id = row.contragent_avr_id;
    if (downloadingIds[id]) return;
    setDownloadingIds(prev => ({ ...prev, [id]: true }));
    await downloadFile(
      `/partner/contragent_avr/document/download/${id}/`,
      `АВР ${displayAvrNum(row) === "—" ? id : displayAvrNum(row)}`,
      {},
      "pdf",
      "get",
      true,
      false
    );
    setDownloadingIds(prev => ({ ...prev, [id]: false }));
  };

  return (
    <div>
      {!remontId && (
        <CollapseBlock className={styles.header}>
          <AvrJournalFilters
            key={filtersKey}
            filters={filters}
            contractors={contractors}
            rowsCount={rows.length}
            isFetching={isFetching}
            onChange={onFilterChange}
            onReset={onReset}
          />
        </CollapseBlock>
      )}
      <CustomTable
        headItems={headItems}
        isFetching={isFetching}
        isEmpty={!rows.length}
        stickyHeader
      >
        {rows.map(row => {
          const signLine = avrSignLine(row);
          return (
            <CustomTableRow key={row.contragent_avr_id} isBinaryDiv={false}>
              <CustomTableCell align="left">
                <div className={styles.cell__main}>ID {row.contragent_avr_id}</div>
                <div>{avrObjectLine(row)}</div>
                {!!row.remont_id && !remontId && (
                  <Link
                    className={styles.cell__link}
                    to={`${PAGES_ROUTE_KEY.singleRemont}/${row.remont_id}`}
                  >
                    ремонт {row.remont_id}
                  </Link>
                )}
              </CustomTableCell>
              <CustomTableCell align="left">
                <div className={styles.cell__num}>
                  <span className={styles.cell__main}>{displayAvrNum(row)}</span>
                  {canEditAvrNum(row) && (
                    <Tooltip title="Номер акта">
                      <IconButton size="small" onClick={() => openNumDialog(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                <div className={styles.cell__meta}>{row.avr_date || "—"}</div>
                <div className={styles.cell__type}>{row.avr_type_name || "—"}</div>
              </CustomTableCell>
              <CustomTableCell align="left">
                <div>{row.contractor_name || "—"}</div>
                {!!row.contractor_bin && (
                  <div className={styles.cell__meta}>БИН {row.contractor_bin}</div>
                )}
                {!!row.company_name && (
                  <div className={styles.cell__meta}>
                    {row.company_name}
                    {!!row.company_bin && ` · БИН ${row.company_bin}`}
                  </div>
                )}
              </CustomTableCell>
              <CustomTableCell align="left">
                <div>{row.agreement_name || "—"}</div>
                {String(row.send_mode || "").toUpperCase() === "WITHOUT_SPEC" && (
                  <div className={styles.cell__meta}>Без спецификации</div>
                )}
              </CustomTableCell>
              <CustomTableCell align="right">
                <strong>
                  <Amount value={Number(row.avr_sum || 0).toFixed(2)} />
                </strong>
                <div className={styles.cell__meta}>
                  {Number(row.is_nds) === 1 ? "С НДС" : "Без НДС"}
                </div>
              </CustomTableCell>
              <CustomTableCell align="left">
                <span className={clsx(styles.status, styles[`status_${row.status_code}`])}>
                  {row.status_name || "—"}
                </span>
                {!!signLine && <div className={styles.cell__meta}>{signLine}</div>}
              </CustomTableCell>
              <CustomTableCell align="center">
                <div className={styles.actions}>
                  {canSignAvr(row) && (
                    <CButton
                      mode="auto"
                      className={styles.actions__btn}
                      loading={signingId === row.contragent_avr_id}
                      onClick={() => handleSign(row)}
                    >
                      Подписать акт
                    </CButton>
                  )}
                  <Clickable
                    className={styles.actions__download}
                    loading={!!downloadingIds[row.contragent_avr_id]}
                    onClick={() => handleDownload(row)}
                  >
                    <DownloadIcon fontSize="small" /> Скачать PDF
                  </Clickable>
                </div>
              </CustomTableCell>
            </CustomTableRow>
          );
        })}
      </CustomTable>

      <Dialog open={!!numDialog} onClose={() => !numSaving && setNumDialog(null)}>
        <DialogTitle>Номер акта</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Этот номер пойдёт в PDF и в 1С. Пока нет подписи и отправки — его можно менять.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Номер"
            value={numDialog?.value || ""}
            onChange={e =>
              setNumDialog(prev => ({ ...prev, value: e.target.value }))
            }
            onKeyDown={e => e.key === "Enter" && saveNum()}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={numSaving} onClick={() => setNumDialog(null)}>
            Отмена
          </Button>
          <CButton mode="auto" loading={numSaving} onClick={saveNum}>
            Сохранить
          </CButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
